import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Badge, Button } from 'react-bootstrap';
import { FaClock, FaUser, FaEdit, FaTrash, FaStar, FaRegStar, FaBan, FaShieldAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';

import Loader from '../components/Loader';
import Message from '../components/Message';
import MarkdownRenderer from '../components/MarkdownRenderer';
import BackToTop from '../components/BackToTop';

import {
  useGetBlogPostBySlugQuery,
  useAdminDeleteBlogPostMutation,
  useAdminToggleFeaturedMutation,
  useAdminToggleRestrictMutation,
} from '../slices/blogsApiSlice';

export default function BlogPostScreen() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const { data: post, isLoading, error } = useGetBlogPostBySlugQuery(slug);

  const [adminDeleteApi, { isLoading: isDeleting }] = useAdminDeleteBlogPostMutation();
  const [adminToggleApi] = useAdminToggleFeaturedMutation();
  const [adminToggleRestrictApi, { isLoading: isRestricting }] = useAdminToggleRestrictMutation();

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <Message variant='danger'>
        {error?.data?.message || error.error}
      </Message>
    );
  }

  const isAuthor = userInfo && post.author && userInfo._id === post.author._id;
  const isAdmin = userInfo && userInfo.isAdmin;

  const dateStr = new Date(post.createdAt).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const handleAdminDelete = async () => {
    if (!window.confirm(`Delete blog post "${post.title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteApi(post._id).unwrap();
      toast.success('Blog post deleted');
      navigate(isAdmin ? `/admin/blogs` : '/blog');
    } catch (err) {
      toast.error(err?.data?.message || 'Delete failed');
    }
  };

  const handleToggleFeatured = async () => {
    try {
      const result = await adminToggleApi(post._id).unwrap();
      toast.success(result.isFeatured ? 'Post featured' : 'Post unfeatured');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update');
    }
  };

  const handleToggleRestricted = async () => {
    try {
      const result = await adminToggleRestrictApi(post._id).unwrap();
      toast.success(result.isRestricted ? 'Post restricted from public view' : 'Post restriction lifted');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update restriction');
    }
  };

  return (
    <>
      <div className='d-flex flex-wrap gap-2' style={{ marginTop: '32px', marginBottom: '16px' }}>
        <Link to='/blog' className='btn btn-light'>
          ← Back to Blog
        </Link>
        {isAdmin && (
          <Link to={`/admin/blogs`} className='btn btn-outline-secondary'>
            ← Back to Manage Blog Posts
          </Link>
        )}
      </div>

      {post.isRestricted && (
        <Message variant='danger'>
          <strong>Restricted by Administrator:</strong> This post has been restricted and is hidden from the public blog.
        </Message>
      )}

      <Card className='mb-4 card--opaque'>
        <Card.Header>
          <h1 className='mb-2'>{post.title}</h1>
          <div className='d-flex flex-wrap gap-3 text-muted'>
            <span><FaUser className='me-1' />{post.author?.firstName || 'Unknown'}</span>
            <span>{dateStr}</span>
            {post.readingTime && (
              <span><FaClock className='me-1' />{post.readingTime} min read</span>
            )}
            {post.isFeatured && (
              <Badge bg='warning' text='dark'><FaStar className='me-1' />Featured</Badge>
            )}
            {post.status === 'draft' && (
              <Badge bg='secondary'>Draft</Badge>
            )}
            {post.isPrivate && (
              <Badge bg='secondary'>Private</Badge>
            )}
            {post.isRestricted && (
              <Badge bg='danger'><FaBan className='me-1' />Restricted</Badge>
            )}
          </div>
          {post.tags && post.tags.length > 0 && (
            <div className='mt-2'>
              {post.tags.map((tag) => (
                <Badge key={tag} bg='secondary' className='me-1'>{tag}</Badge>
              ))}
            </div>
          )}
        </Card.Header>
        <Card.Body>
          {post.featuredImage && (
            <img
              src={`/api/blog/images/${post.featuredImage._id || post.featuredImage}`}
              alt={post.title}
              className='img-fluid rounded mb-4'
              style={{ maxHeight: '400px', width: '100%', objectFit: 'cover' }}
            />
          )}
          <MarkdownRenderer content={post.content} />
        </Card.Body>
      </Card>

      {isAuthor && (
        <div className='mb-3'>
          <Link to={`/me/blogs/edit/${post._id}`}>
            <Button variant='outline-primary' className='me-2'>
              <FaEdit className='me-1' />Edit Post
            </Button>
          </Link>
        </div>
      )}

      {isAdmin && (
        <Card className='mb-4 border-danger card--opaque'>
          <Card.Header className='bg-danger text-white fw-bold'>Admin Moderation Actions</Card.Header>
          <Card.Body className='d-flex flex-wrap gap-2'>
            <Button
              variant={post.isRestricted ? 'success' : 'outline-danger'}
              onClick={handleToggleRestricted}
              disabled={isRestricting}
            >
              {post.isRestricted ? (
                <><FaShieldAlt className='me-1' />Lift Restriction</>
              ) : (
                <><FaBan className='me-1' />Restrict Post</>
              )}
            </Button>
            <Button variant='outline-warning' onClick={handleToggleFeatured}>
              {post.isFeatured ? <><FaRegStar className='me-1' />Unfeature</> : <><FaStar className='me-1' />Feature</>}
            </Button>
            <Button variant='danger' onClick={handleAdminDelete} disabled={isDeleting}>
              <FaTrash className='me-1' />{isDeleting ? 'Deleting…' : 'Delete Post'}
            </Button>
          </Card.Body>
        </Card>
      )}

      <BackToTop heroThreshold={200} />
    </>
  );
}
